import { useCallback, useRef } from "react";
import { hasDismissOwner } from "./dismissOwner";

/**
 * The two handlers a self-rolled backdrop needs so that ONE press means ONE
 * dismissal — the pair `ModalWrapper` and `Modal` carry inline, for every
 * dialog that paints its own `fixed inset-0` scrim instead of using a shell.
 *
 * <p>Spread both onto the backdrop element. The question "did a child own this
 * press?" is asked on the way DOWN (`onMouseDownCapture`), because by `click`
 * the picker list has already closed on its blur and its marker is gone; the
 * answer is then consumed by the `click` that follows. `closable` gates the
 * close the way the callers' own conditions did (`!isSubmitting`,
 * `!showConfirmation`).
 *
 * <p>Why this exists: while an OptionInput list was open it used to cover the
 * viewport with a click shield that lived INSIDE the host, so a backdrop press
 * never reached these handlers at all. The shield is gone (it ate the first
 * click on Save), and the audit of that change found eight dialogs — the
 * view-save popup, the guest popup, the slotting request forms, batch
 * check-in, change-owner — where the press that closed the list now also tore
 * the dialog down, unsaved edits and all.
 */
export function useBackdropDismiss(
  onClose: () => void,
  closable = true,
  /**
   * Where to look for the open child. Defaults to the backdrop element's own
   * subtree, which is right for the usual shape (scrim wraps card). A backdrop
   * that is a SIBLING of its card (`PSaveView`) has no picker inside it and
   * must hand over the common parent instead — asked of the scrim alone the
   * answer is always "no", and the guard is silently inert.
   */
  scope?: () => ParentNode | null,
) {
  const pressDismissedAChild = useRef(false);
  const onMouseDownCapture = useCallback((e: React.MouseEvent) => {
    pressDismissedAChild.current =
      e.target === e.currentTarget && hasDismissOwner(scope ? scope() : (e.currentTarget as Element));
  }, [scope]);
  const onClick = useCallback(
    (e: React.MouseEvent) => {
      if (pressDismissedAChild.current) {
        pressDismissedAChild.current = false;
        return;
      }
      if (closable && e.target === e.currentTarget) onClose();
    },
    [closable, onClose],
  );
  return { onMouseDownCapture, onClick };
}
